import { useState, type FormEvent } from "react";
import { istAnmeldeAdresse } from "@shared/login.js";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { LogIn } from "lucide-react";
import { AuthCardLayout } from "@/components/layout/AuthCardLayout";
import { Button } from "@/components/ui/button";
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { login } from "@/lib/api/auth";
import { importLegacyLocalStorage } from "@/lib/api/maintenance";
import { useDialog } from "@/lib/dialogs/DialogProvider";
import { useAsyncAction } from "@/lib/hooks/useAsyncAction";
import { useSession } from "@/lib/session/SessionContext";
import { homeFor } from "@/lib/session/homeRoute";

/**
 * Die Mandantenkennung der letzten erfolgreichen Anmeldung auf diesem Gerät.
 * Ein Fahrer tippt sie nicht jeden Morgen ab — er bekommt sie einmal als Link
 * (`/login?mandant=<kennung>`), danach steht sie im Feld.
 */
const MANDANT_SPEICHER = "cleverfleet_mandant";

const gemerkterMandant = () => {
  try {
    return localStorage.getItem(MANDANT_SPEICHER) ?? "";
  } catch {
    return "";
  }
};

export function LoginPage() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mandant, setMandant] = useState(() => (searchParams.get("mandant") ?? gemerkterMandant()).trim());
  const { applyBootstrap, refetch } = useSession();
  const { confirm, alert } = useDialog();
  const { run, pending } = useAsyncAction();
  const navigate = useNavigate();

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    run(async () => {
      const anmeldename = email.trim();
      const kennung = mandant.trim();
      const result = await login(anmeldename, password, kennung);
      applyBootstrap(result);

      // Nur nach erfolgreicher Anmeldung merken — ein Tippfehler soll nicht auf
      // dem Gerät bleiben. Wer sich mit einer Adresse anmeldet, braucht die
      // Kennung nicht; sein Eintrag wird auch nicht überschrieben.
      if (kennung) {
        try {
          localStorage.setItem(MANDANT_SPEICHER, kennung);
        } catch {
          // Privater Modus o. Ä.: das Feld bleibt beim nächsten Mal leer, mehr nicht.
        }
      }

      if (result.session?.actorType === "admin") {
        if (localStorage.getItem("cleverfleet_clients")) {
          const shouldImport = await confirm(
            "Es wurden Daten aus der bisherigen lokalen Version gefunden. Möchtest du sie einmalig in die zentrale Datenbank importieren?",
            { title: "Lokale Daten importieren" }
          );
          if (shouldImport) {
            const migration = await importLegacyLocalStorage();
            await refetch();
            await alert(`${migration.imported} Kundendatensätze wurden importiert.`, { title: "Import abgeschlossen" });
          }
        }
      }
      // Ziel aus homeRoute.ts, nicht hier noch einmal ausgeschrieben: der
      // Sonderfall des Bearbeiters oben ist der Datenimport, nicht das Ziel.
      navigate(result.session ? homeFor(result.session) : "/login", { replace: true });
    });
  };

  // Sobald etwas dasteht, das keine Adresse ist, gehört die Kennung dazu; ein
  // vorbelegtes Feld (Link oder Gerät) bleibt sichtbar, damit niemand rätselt,
  // gegen welchen Mandanten er sich gerade anmeldet.
  const mandantNoetig = Boolean(mandant) || (email.trim().length > 0 && !istAnmeldeAdresse(email));

  return (
    <AuthCardLayout>

      <form onSubmit={handleSubmit}>
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="login-email">E-Mail-Adresse oder Benutzername</FieldLabel>
            {/* type="text", nicht "email" (S1.6): Mitglieder aus dem Onboarding haben
                oft nur einen Benutzernamen, und der Browser ließ das Formular mit
                type="email" gar nicht erst abschicken. */}
            <Input
              id="login-email"
              type="text"
              autoComplete="username"
              placeholder="z. B. name@cleverfleet.de"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </Field>

          {/* Nur wo sie gebraucht wird: ein Benutzername ist erst zusammen mit dem
              Mandanten eindeutig. Wer eine Adresse eintippt, sieht das Feld nie. */}
          {mandantNoetig ? (
            <Field>
              <FieldLabel htmlFor="login-mandant">Mandantenkennung</FieldLabel>
              <Input
                id="login-mandant"
                type="text"
                autoComplete="organization"
                placeholder="z. B. musterlogistik"
                required
                value={mandant}
                onChange={(event) => setMandant(event.target.value)}
              />
            </Field>
          ) : null}

          <Field>
            <div className="flex items-center justify-between">
              <FieldLabel htmlFor="login-pass">Passwort</FieldLabel>
              <Link to="/forgot-password" className="text-xs text-primary no-underline hover:underline">
                Passwort vergessen?
              </Link>
            </div>
            <Input
              id="login-pass"
              type="password"
              placeholder="Dein Passwort"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </Field>

          <Button type="submit" className="w-full hover:bg-primary-dark" disabled={pending}>
            Anmelden <LogIn data-icon className="size-4" aria-hidden="true" />
          </Button>
        </FieldGroup>
      </form>
    </AuthCardLayout>
  );
}
